import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { API_WEBSITE_GATE } from '../helpers/api_constant';
import { SafeWindow } from '../utils/safe-window';

const COOKIE_PREFIX = 'sp_wg_';
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Tracks which organization's gate applies to the current page and whether this
 * browser already completed that org's current form version (a cookie, so clearing
 * cookies or a new private window asks again).
 */
@Injectable({
  providedIn: 'root',
})
export class WebsiteGateService {
  win = SafeWindow.get();
  private orgIdStore = new BehaviorSubject<number | null>(null);
  orgId$ = this.orgIdStore.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Org marketing sites are gated by the site's own org. The shared QR/unpublished
   * registration hosts have no site org, so the tournament/event being opened decides.
   */
  isOrgSiteHost(): boolean {
    if (!this.win) {
      return false;
    }
    const hostname = window.location.hostname;
    return (
      !hostname.includes('qrc-registration') &&
      !hostname.includes(environment.unpublishOrgTournamentRegisterSubdomain)
    );
  }

  setSiteOrg(orgId: number | null | undefined) {
    if (orgId && this.isOrgSiteHost()) {
      this.setOrg(orgId);
    }
  }

  setRegistrationOrg(orgId: number | null | undefined) {
    if (orgId && !this.isOrgSiteHost()) {
      this.setOrg(orgId);
    }
  }

  getConfig(orgId: number): Observable<any> {
    return this.http.get(
      `${environment.API}${API_WEBSITE_GATE.PUBLIC}/${orgId}`,
    );
  }

  submit(
    orgId: number,
    payload: { formVersion: string; answers: Record<string, unknown> },
  ): Observable<any> {
    return this.http.post(
      `${environment.API}${API_WEBSITE_GATE.PUBLIC}/${orgId}/submit`,
      payload,
    );
  }

  isCompleted(orgId: number, formVersion: string): boolean {
    if (!this.win) {
      return false;
    }
    const name = `${COOKIE_PREFIX}${orgId}=`;
    const cookie = document.cookie
      .split('; ')
      .find((item) => item.startsWith(name));
    return decodeURIComponent(cookie?.slice(name.length) ?? '') === formVersion;
  }

  markCompleted(orgId: number, formVersion: string) {
    if (!this.win) {
      return;
    }
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_PREFIX}${orgId}=${encodeURIComponent(formVersion)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  }

  private setOrg(orgId: number) {
    if (this.orgIdStore.value !== orgId) {
      this.orgIdStore.next(orgId);
    }
  }
}
